import { AuthService } from '../../modules/auth/auth.service';

/** Resolve branch id for API calls; falls back to the logged-in user's branch. */
export function resolveBranchId(
  auth: AuthService,
  value?: string | number | null,
): number {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }

  const userBranch = auth.currentBranchId();
  return userBranch > 0 ? userBranch : 0;
}

export interface BranchLookup {
  id: string;
  name: string;
}

export interface LocationLookup {
  branchId: string;
}

/** True when the logged-in user is tied to a single branch (hide "All Branches"). */
export function isBranchFilterLocked(auth: AuthService): boolean {
  return auth.currentBranchId() > 0;
}

/** Branches visible to the current user. */
export function scopeBranches<T extends BranchLookup>(
  auth: AuthService,
  branches: ReadonlyArray<T>,
): T[] {
  const userBranchId = auth.currentBranchId();
  if (userBranchId <= 0) {
    return [...branches];
  }

  return branches.filter((branch) => Number(branch.id) === userBranchId);
}

/** Store locations visible to the current user. */
export function scopeLocations<T extends LocationLookup>(
  auth: AuthService,
  locations: ReadonlyArray<T>,
): T[] {
  const userBranchId = auth.currentBranchId();
  if (userBranchId <= 0) {
    return [...locations];
  }

  return locations.filter(
    (location) => Number(location.branchId) === userBranchId,
  );
}

/** Optional branchId query param for GET endpoints that support it. */
export function branchQueryParams(
  auth: AuthService,
): Record<string, number> | undefined {
  const branchId = auth.currentBranchId();
  return branchId > 0 ? { branchId } : undefined;
}

/** Branch id for dropdown filters (options use branch.id as value). */
export function defaultBranchFilterId(
  auth: AuthService,
  branches?: ReadonlyArray<BranchLookup>,
): string {
  const userBranchId = auth.currentBranchId();
  if (userBranchId <= 0) {
    return 'all';
  }

  const match = branches?.find((b) => b.id === String(userBranchId));
  return match?.id ?? String(userBranchId);
}

/** Reset branch id filter respecting branch lock. */
export function resetBranchFilterId(
  auth: AuthService,
  branches?: ReadonlyArray<BranchLookup>,
): string {
  return defaultBranchFilterId(auth, branches);
}

/** Branch name for dropdown filters (options use branch.name as value). */
export function defaultBranchFilter(
  auth: AuthService,
  branches?: ReadonlyArray<BranchLookup>,
): string {
  const userBranchId = auth.currentBranchId();
  if (userBranchId <= 0) {
    return 'all';
  }

  const match = branches?.find((b) => b.id === String(userBranchId));
  return match?.name ?? String(userBranchId);
}

/** Reset branch filter respecting branch lock. */
export function resetBranchFilter(
  auth: AuthService,
  branches?: ReadonlyArray<BranchLookup>,
): string {
  return defaultBranchFilter(auth, branches);
}

/** Defense-in-depth: drop rows that belong to another branch when branchId is known. */
export function filterRowsByUserBranch<T>(
  auth: AuthService,
  rows: T[],
  pickBranchId: (row: T) => string | undefined,
): T[] {
  const userBranchId = auth.currentBranchId();
  if (userBranchId <= 0) {
    return rows;
  }

  const expected = String(userBranchId);
  return rows.filter((row) => {
    const branchId = pickBranchId(row)?.trim();
    if (!branchId) {
      return true;
    }
    return branchId === expected;
  });
}

/** Match UI branch filter against row branch name and/or id. */
export function matchesBranchFilter(
  filter: string,
  branchName?: string,
  branchId?: string,
  branches?: ReadonlyArray<BranchLookup>,
): boolean {
  if (!filter || filter === 'all') {
    return true;
  }

  const normalizedName = (branchName ?? '').trim();
  if (normalizedName && normalizedName === filter) {
    return true;
  }

  const normalizedId = (branchId ?? '').trim();
  if (normalizedId && normalizedId === filter) {
    return true;
  }

  const selected = branches?.find((b) => b.id === filter || b.name === filter);
  if (selected) {
    const selectedName = selected.name.trim().toLowerCase();
    const rowName = normalizedName.toLowerCase();
    if (
      rowName &&
      selectedName &&
      (rowName === selectedName ||
        rowName.includes(selectedName) ||
        selectedName.includes(rowName))
    ) {
      return true;
    }
    if (normalizedId && normalizedId === selected.id) {
      return true;
    }
  }

  if (!selected) {
    return normalizedId ? normalizedId === filter : true;
  }

  return normalizedName === selected.name || normalizedId === selected.id;
}
